import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { SocketChannelNamespaceType } from "@/constant/export/SocketChannelNamespaceType";
import { getChannel } from "@/utils/socketManager/socketManager";
import { fetchSegmentsByMarketId } from "@/store/slices/segments/segments.thunk";
import { AIAssistantEventType } from "../../constants/AIAssistantEventType";
import { useAssistantAbly } from "../../contexts/AIAssistantAblyContext";
import { setTaskStatusMessage } from "../../state/actions";

const AssistantSocketListener = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { ablyClient, clientId } = useAssistantAbly();
  const selectedMarkets = useSelector((state) =>
    Array.isArray(state?.userSelection?.markets)
      ? state.userSelection.markets
      : []
  );
  const selectedMarketIds = useMemo(
    () => selectedMarkets.map((id) => Number(id)),
    [selectedMarkets]
  );
  const marketsById = useSelector(
    (state) => state?.activeGroup?.markets?.markets?.entities || {}
  );

  useEffect(() => {
    if (!ablyClient || !clientId) return undefined;
    const channel = getChannel(
      ablyClient,
      clientId,
      SocketChannelNamespaceType.AI_ASSISTANT,
    );
    if (!channel) return undefined;

    const handleStatus = (message) => {
      let payload = message?.data;
      if (typeof payload === "string") {
        try {
          payload = JSON.parse(payload);
        } catch (err) {
          payload = { taskMessage: payload };
        }
      }
      if (!payload) return;
      const statusPayload =
        typeof payload === "object" ? payload : { taskMessage: String(payload) };
      const statusType = statusPayload.type || "task_status";
      if (statusType === "task_status_end") {
        dispatch(setTaskStatusMessage({ type: "task_status_end" }));
        return;
      }
      dispatch(
        setTaskStatusMessage({
          type: statusType,
          stage: statusPayload.stage,
          taskTitle:
            statusPayload.taskTitle ||
            statusPayload.stage ||
            "Working on it...",
          taskMessage:
            statusPayload.taskMessage ||
            statusPayload.message ||
            statusPayload.text ||
            "",
        }),
      );
    };

    const handleAIMessage = (message) => {
      let payload = message?.data;
      if (typeof payload === "string") {
        try {
          payload = JSON.parse(payload);
        } catch (err) {
          return;
        }
      }
      if (!payload || typeof payload !== "object") return;

      const messageType = payload.type;
      if (messageType === "navigate") {
        const navLink =
          payload.link ??
          payload.url ??
          payload?.payload?.link ??
          payload?.payload?.url ??
          payload?.data?.link ??
          payload?.data?.url;
        if (!navLink || typeof navLink !== "string") return;

        try {
          if (navLink.startsWith("http")) {
            const url = new URL(navLink);
            const isSameOrigin = url.origin === window.location.origin;
            if (isSameOrigin) {
              navigate(url.pathname + url.search);
            } else {
              window.location.href = navLink;
            }
          } else {
            navigate(navLink);
          }
        } catch (error) {
          if (!navLink.startsWith("http")) {
            navigate(navLink);
          }
        }
        return;
      }

      if (messageType === "segments_changed") {
        const normalizeMarketIds = (marketIds) =>
          Array.from(
            new Set(
              (marketIds || []).map((id) => Number(id)).filter(Number.isFinite)
            )
          ).sort((a, b) => a - b);
        const eventMarketIds = normalizeMarketIds(
          payload.marketIds ??
            payload?.payload?.marketIds ??
            payload.geoIds ??
            payload?.payload?.geoIds
        );
        if (!eventMarketIds.length) return;

        const normalizedSelectedMarketIds = normalizeMarketIds(selectedMarketIds);
        if (
          normalizedSelectedMarketIds.length > 0 &&
          !eventMarketIds.some((id) =>
            normalizedSelectedMarketIds.includes(id)
          )
        ) {
          return;
        }

        const marketIdsToRefresh =
          normalizedSelectedMarketIds.length > 0
            ? normalizedSelectedMarketIds
            : eventMarketIds;
        const marketCodes = marketIdsToRefresh
          .map((id) => marketsById?.[id]?.identity)
          .filter(Boolean);
        dispatch(fetchSegmentsByMarketId({ marketIds: marketIdsToRefresh, marketCodes }));
      }
    };

    channel.subscribe(AIAssistantEventType.ASSISTANT_TASK_STATUS, handleStatus);
    channel.subscribe(AIAssistantEventType.AI_MESSAGE, handleAIMessage);

    return () => {
      channel.unsubscribe(
        AIAssistantEventType.ASSISTANT_TASK_STATUS,
        handleStatus,
      );
      channel.unsubscribe(AIAssistantEventType.AI_MESSAGE, handleAIMessage);
    };
  }, [
    ablyClient,
    clientId,
    dispatch,
    navigate,
    selectedMarketIds,
    marketsById,
  ]);

  return null;
};

export default AssistantSocketListener;
